import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { ApiRetailerPaymentType } from '@vend/types'
import {
  Button,
  Popover,
  PopoverList,
  PopoverListItem,
  WithPopover,
} from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { capitaliseFirst } from '@vend/utilities/dist/strings/capitaliseFirst'
import type { PaymentMethod } from '@vend/business-components/dist/components/LspayCnp/queries'
import { usePMOFFeature } from '@vend/business-components'
import { getFeatureFromCache } from '../../../../util/feature-cache'
import { useAppSelector } from '../../../../state/hooks'
import {
  selectOutstandingBalance,
  selectSaleCustomer,
} from '../../../../state/slices/currentSale/currentSaleSlice'
import { isValidCustomer } from '../../../../functions/sale'
import {
  useRegisterManager,
  useUserManager,
  useWebRegisterStatusManager,
} from '../../../../ng-compat/hooks'
import { PaymentData } from '../PaymentPanel'
import { fetchLSPayCustomerPaymentMethods } from '../../../../functions/payment-types/lspayPmof'
import { PERMISSIONS } from '../../../../constants'
import { selectTrainingModeEnabled } from '../../../../state/slices/posState/posStateSlice'

export type LSPayPMOFProps = {
  // Lightspeed Payments system payment type to use for processing
  paymentType?: ApiRetailerPaymentType
  // Trigger the payment flow when clicked
  pay: (paymentType: ApiRetailerPaymentType, paymentData: PaymentData) => void
  // Optional disabled state (e.g., amount invalid, offline, etc.)
  disabled?: boolean
}
/**
 * LSPayPMOF (Lightspeed Payments - Payment Method On File)
 */
export const LSPayPMOF: FunctionComponent<LSPayPMOFProps> = ({
  paymentType,
  pay,
  disabled,
}) => {
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const outlet = useRegisterManager().getActiveOutlet()
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const userManager = useUserManager()
  const validCustomer = isValidCustomer(saleCustomer)

  const featureFlagEnabled = getFeatureFromCache('enable_customer_saved_cards')
  const isTrainingMode = useAppSelector(selectTrainingModeEnabled)
  const webRegisterStatusManager = useWebRegisterStatusManager()
  const { isEnabled: featureEnabled } = usePMOFFeature()
  const userPermissionGranted = userManager.hasPermission(
    PERMISSIONS.LSPAY_PMOF_ADD_EDIT_USE
  )

  const enabled =
    featureFlagEnabled &&
    featureEnabled &&
    userPermissionGranted &&
    !isTrainingMode &&
    webRegisterStatusManager.isOnline()

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[] | []>([])
  const [active, setActive] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        if (!enabled || !validCustomer || !saleCustomer?.id || !outlet?.id) {
          if (!cancelled) setPaymentMethods([])
          return
        }
        const methods = await fetchLSPayCustomerPaymentMethods(
          saleCustomer.id,
          outlet.id
        )
        if (!cancelled) setPaymentMethods(methods ?? [])
      } catch {
        if (!cancelled) setPaymentMethods([])
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [enabled, validCustomer, saleCustomer?.id, outlet?.id])

  useEffect(() => {
    if (disabled) {
      setActive(false)
    }
  }, [disabled])

  const handlePay = useCallback(
    (paymentMethodOnFileId: string) => {
      if (disabled || !paymentType) return
      const paymentData = {
        lspayFlow: 'pmof',
        paymentMethodOnFileId,
      } as PaymentData
      pay(paymentType, paymentData)
    },
    [disabled, paymentType, pay]
  )

  const dots = '\u2022\u2022\u2022\u2022'

  // Shared button configuration
  const buttonConfig = useMemo(
    () => ({
      variant: (outstandingBalance < 0 ? 'no' : 'go') as 'no' | 'go',
      className: 'vd-btn vd-btn--jumbo vd-pt5 vd-pb5 vd-mt4',
      disabled,
      'data-track': `payment-type-${paymentType?.name}-pmof`,
    }),
    [outstandingBalance, disabled, paymentType?.name]
  )

  if (!enabled || !paymentType || paymentMethods.length === 0) {
    return null
  }

  // Render a popover list which is positioned above the button
  return (
    <WithPopover direction="topLeft" className="pmof-popover-wrapper">
      <Button
        {...buttonConfig}
        onClick={() => {
          if (!disabled) setActive(true)
        }}
      >
        <T
          _str="Pay with Saved Payment Method"
          _context="Pay with saved payment method button"
        />
      </Button>
      {active && !disabled && (
        <Popover
          onClose={() => setActive(false)}
          className="vd-popover--with-list"
        >
          <PopoverList>
            {paymentMethods.map((m, index) => {
              const brand =
                m.type === 'card'
                  ? capitaliseFirst(m.details?.brand || '')
                  : capitaliseFirst(m.details?.accountType || '')
              const last4 = m.details?.last4 || ''
              return (
                <PopoverListItem
                  key={m.id}
                  index={index}
                  onItemSelected={() => {
                    handlePay(m.id)
                    setActive(false)
                  }}
                >
                  {brand} {dots} {last4}
                </PopoverListItem>
              )
            })}
          </PopoverList>
        </Popover>
      )}
    </WithPopover>
  )
}
